import React, { useState, useEffect } from 'react';
import { formsAPI } from '../services/api';

interface SubmissionForModal {
  submissionId: string;
  memberInfo: {
    firstName: string;
    lastName: string;
    email?: string;
    phone: string;
  };
}

interface SendPatientLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  submission: SubmissionForModal | null;
}

const DEFAULT_SMS_PREFIX = '+1';

const normalizeSmsRecipient = (value: string): string => {
  const trimmedValue = value.trim();
  const digits = trimmedValue.replace(/\D/g, '').slice(0, 15);

  if (!digits) {
    return DEFAULT_SMS_PREFIX;
  }

  if (trimmedValue.startsWith('+')) {
    return `+${digits}`;
  }

  if (digits.length <= 10) {
    return `${DEFAULT_SMS_PREFIX}${digits}`;
  }

  return `+${digits}`;
};

const isValidSmsRecipient = (value: string): boolean => /^\+\d{8,15}$/.test(value.trim());

const SendPatientLinkModal: React.FC<SendPatientLinkModalProps> = ({ isOpen, onClose, submission }) => {
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [recipient, setRecipient] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (submission) {
      setRecipient(
        channel === 'email'
          ? (submission.memberInfo.email || '')
          : normalizeSmsRecipient(submission.memberInfo.phone || '')
      );
    }
    setStatus('idle');
    setErrorMessage('');
    setGeneratedLink('');
    setCopied(false);
  }, [submission, channel]);

  if (!isOpen || !submission) return null;

  const smsRecipientIsValid = channel === 'sms' ? isValidSmsRecipient(recipient) : true;
  const formattedRecipient = channel === 'sms' ? normalizeSmsRecipient(recipient) : recipient.trim();

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formattedRecipient) return;
    if (channel === 'sms' && !smsRecipientIsValid) {
      setStatus('error');
      setErrorMessage('SMS number must start with + and contain 8 to 15 digits.');
      return;
    }

    setSending(true);
    setStatus('idle');
    setErrorMessage('');

    try {
      const result = await formsAPI.sendPatientLink(submission.submissionId, channel, formattedRecipient);
      if (result.success && result.sent) {
        setStatus('success');
        setGeneratedLink(result.accessLink || '');
      } else if (result.success && !result.sent) {
        setStatus('error');
        setErrorMessage(result.error || 'Message service not configured. You can copy the link below.');
        setGeneratedLink(result.accessLink || '');
      } else {
        setStatus('error');
        setErrorMessage('Failed to send link');
      }
    } catch (error: any) {
      setStatus('error');
      setErrorMessage(error?.response?.data?.error || 'An unexpected error occurred');
    } finally {
      setSending(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setStatus('idle');
    setErrorMessage('');
    setGeneratedLink('');
    setRecipient('');
    setCopied(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-800">Send Form Link</h2>
            <button onClick={handleClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Send a pre-filled form to <strong>{submission.memberInfo.firstName} {submission.memberInfo.lastName}</strong>
          </p>
        </div>

        <div className="p-6">
          {status === 'success' ? (
            <div className="text-center py-2">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Link Sent!</h3>
              <p className="text-gray-500 mt-1 text-sm">
                The form link has been sent via {channel === 'email' ? 'email' : 'SMS'} to <strong>{recipient}</strong>.
              </p>
              {generatedLink && (
                <div className="mt-4 text-left">
                  <p className="text-xs text-gray-400 mb-1">Or copy this link to share manually:</p>
                  <div className="flex items-center gap-2">
                    <input type="text" readOnly value={generatedLink} className="flex-1 text-xs px-3 py-2 border rounded-lg bg-gray-50 truncate" />
                    <button onClick={handleCopyLink} className="text-xs px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 whitespace-nowrap font-medium">
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}
              <button onClick={handleClose} className="mt-5 px-5 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSend}>
              {/* Channel Toggle */}
              <div className="flex gap-2 mb-5">
                <button
                  type="button"
                  onClick={() => setChannel('email')}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2 ${
                    channel === 'email' ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => setChannel('sms')}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2 ${
                    channel === 'sms' ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  SMS
                </button>
              </div>

              {/* Recipient Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {channel === 'email' ? 'Email Address' : 'Phone Number'}
                </label>
                <input
                  type={channel === 'email' ? 'email' : 'tel'}
                  value={recipient}
                  onChange={(e) => {
                    if (channel === 'sms') {
                      setRecipient(normalizeSmsRecipient(e.target.value));
                      return;
                    }
                    setRecipient(e.target.value);
                  }}
                  placeholder={channel === 'email' ? 'patient@email.com' : '+15551234567'}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  required
                />
                {channel === 'sms' && (
                  <p className="text-xs text-gray-400 mt-1">Starts with +1 by default. You can change the country code, but only digits are allowed after the +.</p>
                )}
              </div>

              {/* Error */}
              {status === 'error' && (
                <div className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-100">
                  {errorMessage}
                </div>
              )}

              {/* Generated link fallback when send fails */}
              {generatedLink && status === 'error' && (
                <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                  <p className="text-xs text-yellow-700 font-medium mb-1.5">Link generated — copy to send manually:</p>
                  <div className="flex items-center gap-2">
                    <input type="text" readOnly value={generatedLink} className="flex-1 text-xs px-2 py-1.5 border rounded-lg bg-white truncate" />
                    <button type="button" onClick={handleCopyLink} className="text-xs px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium whitespace-nowrap">
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={handleClose} disabled={sending} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sending || !formattedRecipient || !smsRecipientIsValid}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {sending ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send via {channel === 'email' ? 'Email' : 'SMS'}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendPatientLinkModal;
